import {
    Component,
    EventEmitter,
    Input, OnChanges,
    Output, SimpleChanges,
    ViewChild
} from '@angular/core';

import {CommonModule} from '@angular/common';
import {ClientService} from '../../shared/_services/client.service';
import {PageService} from '../../shared/_services/page.service';
import {OnDestroyPage} from '../../shared/_inherited/ondestroy.page';
import {Base, Thread, ThreadMessage} from '@nxt/model-core';
import {MessagingService} from '../_services/messaging.service';
import {MessageItemComponent} from '../../../nxto/src/app/threads/_components/message-item.component';
import {ScrollableGenericList} from './scrollable-generic-list';
import {FireService, IFirestoreQuery} from '../_services/fire.service';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        ScrollableGenericList,
        MessageItemComponent
    ],
    selector: 'scrollable-thread-triggered-list',
    template: `

        <message-item
            *ngIf="sourceMsg"
            [index]="-1"
            [editable]="false"
            (onSelect)="mSvc.msg$.next($event)"
            [selected]="(mSvc.msg$|async)?.id === sourceMsg.id"
            [message]="sourceMsg"
            [parent]="parent"></message-item>

        <scrollable-generic-list
            #itemList
            [pageSize]="15"
            [itemTemplate]="itemTemplate"
            [hideEmpty]="!thread._exists || !!sourceMsg"
            label="Messages"
            [autoStart]="true"
            [loadAll]="false"
            [watch]="true"
            [path]="path"
            [baseQuery]="query"
            (onItemsLoaded)="onItemsLoaded.emit()"
        ></scrollable-generic-list>

        <ng-template let-item="item" let-items="items" let-i="i" #itemTemplate>
            <message-item
                [index]="i"
                (onSelect)="mSvc.msg$.next($event)"
                [selected]="(mSvc.msg$|async)?.id === item.id"
                (onDelete)="handleDelete($event)"
                [message]="item"
                [thread]="thread"
                [parent]="parent || thread.object || (mSvc.context$|async)"></message-item>
        </ng-template>
    `
})
export class ScrollableThreadTriggeredList extends OnDestroyPage implements OnChanges {
    @ViewChild('itemList', {static:true}) itemList: ScrollableGenericList;
    @Output() onItemsLoaded: EventEmitter<any> = new EventEmitter<any>();
    @Input() thread: Thread;
    @Input() parent: Base;
    path: string;
    query: IFirestoreQuery[];
    sourceMsg: ThreadMessage;

    get firstMessageSubject(): string {
        // sourceMsg is the cross-client broker message shown above the list.
        // If it exists, it's conceptually the "first" message in context.
        // Otherwise fall back to the last item in the list (list is ordered desc, so last = oldest).
        if (this.sourceMsg?.subject) {
            return this.sourceMsg.subject;
        }
        const items = this.itemList?.items;
        if (items?.length) {
            return items[items.length - 1]?.subject || '';
        }
        return '';
    }

    constructor(
        public cSvc: ClientService,
        public pSvc: PageService,
        public mSvc: MessagingService,
        private fSvc: FireService
    ) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.thread?.currentValue?.id && changes.thread?.currentValue?.id !== changes.thread?.previousValue?.id) {
            this.loadThread();
        }
    }

    loadThread() {
        if (this.cSvc.client_id) {
            if (!this.thread?._docRef && this.thread?.id){
                this.thread._docRef = this.cSvc.client$.getValue()._docRef.collection('threads').doc(this.thread.id);
            }
            this.path = `clients/${this.cSvc.client_id}/threadsmsgs`;
            this.query = [
                {name: 'where', args: ['tRef','==', this.thread._docRef]},
                {name: 'orderBy', args: ['date', 'desc']}
            ];
            this.loadSourceMsg();
        }
    }

    async loadSourceMsg() {
        try {
            if (this.parent['metadata']['msgRef']) {
                let docRef = this.parent['metadata']['msgRef'].get ? this.parent['metadata']['msgRef'] : this.fSvc.firestore.doc(this.parent['metadata']['msgRef']);
                const msg = new ThreadMessage(await docRef.get());
                const msgClientId = docRef.path?.split('/')?.[1];
                if (msgClientId !== this.cSvc.client_id) {
                    this.sourceMsg = msg;
                }
            }
        } catch (e) {}
    }

    handleDelete(msg: ThreadMessage) {
        if (!this.itemList.items?.length || (this.itemList.items?.find(i => i.id === msg.id) && this.itemList.items?.length === 1)) {
            this.thread.delete();
        }
    }

    print() {
        let styles: string = 'styles.css';
        let tags = document.getElementsByTagName('link');
        for (let i = 0; i < tags.length; i++) {
            if (tags[i].href.match(/styles.css$/)) {
                styles = tags[i].href;
                break;
            }
        }
        if (!styles) {
            styles = `${window.location.origin}/styles.css`;
        }
        var mywindow = window.open('', 'PRINT', 'height=800,width=1160');
        mywindow.document.write(`<html><head><title>Thread Details</title><link rel="stylesheet" href="${styles}"></head>`);
        mywindow.document.write('</head><body >');
        mywindow.document.write(this.itemList.eRef.nativeElement.innerHTML.replace(this.itemList.class, ''));
        mywindow.document.write('</body></html>');
        mywindow.document.close(); // necessary for IE >= 10
        mywindow.focus(); // necessary for IE >= 10*/
    }
}
